import React from "react";
import { motion } from "framer-motion";

const designs = {
  ai: [
    { gradient: "from-cyan-600 via-blue-700 to-indigo-900", accent: "#06b6d4", icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" },
    { gradient: "from-violet-600 via-purple-700 to-slate-900", accent: "#8b5cf6", icon: "M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z" },
    { gradient: "from-emerald-600 via-teal-700 to-slate-900", accent: "#14b8a6", icon: "M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" },
    { gradient: "from-amber-600 via-orange-700 to-slate-900", accent: "#f59e0b", icon: "M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H6V4h2v7l2.5-1.5L13 11V4h5v16z" },
  ],
  web: [
    { gradient: "from-rose-600 via-pink-700 to-slate-900", accent: "#e11d48", icon: "M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" },
  ]
};

const gradients = [
  "from-cyan-500/20 via-blue-500/10 to-transparent",
  "from-violet-500/20 via-purple-500/10 to-transparent",
  "from-emerald-500/20 via-teal-500/10 to-transparent",
  "from-amber-500/20 via-orange-500/10 to-transparent",
];

const generateDecorativeCircles = (count) => {
  const circles = [];
  for (let i = 0; i < count; i++) {
    circles.push({
      cx: 15 + Math.random() * 70,
      cy: 15 + Math.random() * 70,
      r: 5 + Math.random() * 20,
      delay: Math.random() * 3,
    });
  }
  return circles;
};

const ProjectPlaceholder = ({ project, index = 0 }) => {
  const cat = project.category === 'ai' ? 'ai' : 'web';
  const designSet = designs[cat];
  const design = designSet[index % designSet.length];
  const circles = React.useMemo(() => generateDecorativeCircles(5), []);

  return (
    <svg viewBox="0 0 400 250" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`bg-grad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={design.gradient.split(' ')[0].replace('from-', '#')} />
          <stop offset="100%" stopColor={design.gradient.split(' ').pop().replace('to-', '#')} />
        </linearGradient>
        <radialGradient id={`glow-${index}`} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor={design.accent} stopOpacity="0.3" />
          <stop offset="100%" stopColor={design.accent} stopOpacity="0" />
        </radialGradient>
        <pattern id={`grid-${index}`} width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </pattern>
        <filter id={`glow-filter-${index}`}>
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      <rect width="400" height="250" fill={`url(#bg-grad-${index})`} />
      <rect width="400" height="250" fill={`url(#grid-${index})`} />
      <rect width="400" height="250" fill={`url(#glow-${index})`} />

      {/* Decorative circles */}
      {circles.map((c, i) => (
        <motion.circle
          key={i}
          cx={c.cx * 4}
          cy={c.cy * 2.5}
          r={c.r}
          fill="none"
          stroke={design.accent}
          strokeWidth="1"
          strokeOpacity="0.15"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: [0.8, 1.1, 0.8], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 4 + c.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {/* Main icon */}
      <motion.g
        transform="translate(200, 110)"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <circle cx="0" cy="0" r="48" fill="rgba(255,255,255,0.08)" />
        <circle cx="0" cy="0" r="36" fill="rgba(255,255,255,0.06)" />
        <path d={design.icon} fill="rgba(255,255,255,0.5)" transform="scale(1.8) translate(-12, -12)" />
      </motion.g>

      {/* Bottom bar decoration */}
      <rect x="0" y="220" width="400" height="30" fill="rgba(0,0,0,0.2)" />
      <motion.rect
        x="24" y="228" width="80" height="4" rx="2"
        fill={design.accent} fillOpacity="0.5"
        initial={{ width: 0 }}
        animate={{ width: 80 }}
        transition={{ duration: 1, delay: 0.5 }}
      />
      <motion.rect
        x="24" y="238" width="120" height="3" rx="1.5"
        fill="rgba(255,255,255,0.2)"
        initial={{ width: 0 }}
        animate={{ width: 120 }}
        transition={{ duration: 1, delay: 0.7 }}
      />
      <motion.rect
        x="340" y="228" width="40" height="14" rx="3"
        fill={design.accent} fillOpacity="0.4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 0.5, delay: 1 }}
      />
    </svg>
  );
};

export default ProjectPlaceholder;
